import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Calendar, Upload, X, Loader, Link, Search, Check } from "lucide-react";
import { useAlert } from "components/UI_components/Alert";
import { BASE_URL } from "utils/constant";
import { resolveTransactionFeedback } from "utils/transactionFeedback";
import { getLinkedTasksBySite } from "utils/linkedTasks";
import {
  buildGeneralTransactionTaskUpdate,
  createLinkedTaskUpdate,
} from "utils/taskAutoUpdates";

export default function OtherPaymentForm({ onClose }) {
  const [transactionType, setTransactionType] = useState("");
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [remarks, setRemarks] = useState("");
  const [image, setImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaved, setIsSaved] = useState(false); // New state to track if already saved
  const [otherTransactionCatagory, setOtherTransactionCatagory] =
    useState(null);
  const { addAlert } = useAlert();
  // Get current date for date picker default
  const currentDate = new Date();
  const [selectedDate, setSelectedDate] = useState(currentDate);
  const formattedCurrentDate = `${currentDate.getFullYear()}-${(
    currentDate.getMonth() + 1
  )
    .toString()
    .padStart(2, "0")}-${currentDate.getDate().toString().padStart(2, "0")}`;

  const [transactionDate, setTransactionDate] = useState(formattedCurrentDate);

  // BoQ linking states
  const [boqItems, setBoqItems] = useState([]);
  const [showBoQModal, setShowBoQModal] = useState(false);
  const [selectedBoQ, setSelectedBoQ] = useState(null);
  const [boqSearchQuery, setBoqSearchQuery] = useState("");

  const transactionTypes = [
    "Equipments",
    "Electricity",
    "Transport",
    "Municipality/Panchayat",
    "Water Bill",
  ];
  const formatAmountPreview = (value) => {
    const numericValue = Number(value);
    if (value === "" || Number.isNaN(numericValue)) {
      return "Rs. 0";
    }
    return `Rs. ${numericValue.toLocaleString("en-IN")}`;
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const removeImage = () => {
    setImage(null);
    setPreviewUrl(null);
  };

  const toggleDatePicker = () => {
    setShowDatePicker(!showDatePicker);
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setTransactionDate(newDate);
    setShowDatePicker(false);
  };

  const handleNowButtonClick = () => {
    setTransactionDate(formattedCurrentDate);
    setShowDatePicker(false);
  };

  const openBoQModal = () => {
    setBoqSearchQuery("");
    setShowBoQModal(true);
  };

  const closeBoQModal = () => {
    setShowBoQModal(false);
    setBoqSearchQuery("");
  };

  const handleBoQSelect = (item) => {
    setSelectedBoQ(item);
    closeBoQModal();
  };

  const handleSave = async () => {
    // Prevent multiple submissions
    if (!transactionType || !amount || isLoading || isSaved) return;

    setIsLoading(true);
    setIsSaved(true); // Mark as saved to prevent future clicks

    const site = localStorage.getItem("site");

    const formData = new FormData();
    formData.append("transaction_type", transactionType);
    formData.append("transaction_date", transactionDate);
    formData.append("amount", parseInt(amount));
    formData.append("transaction_method", paymentMethod);
    formData.append("comments", remarks);
    formData.append("transaction_catagory_id", otherTransactionCatagory);
    formData.append("site", parseInt(site));

    if (selectedBoQ?.task) {
      formData.append("task_id", selectedBoQ.task);
    }

    if (image) {
      formData.append("ireceipt_image", image);
    }

    try {
      const accessToken = localStorage.getItem("access");
      const response = await fetch(
        `${BASE_URL}/finance/create-general-transaction/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData,
        },
      );
      const data = await response.json();
      const feedback = resolveTransactionFeedback(data, {
        successMessage: "Payment created successfully",
        errorMessage: "Failed to create payment",
      });

      if (feedback.success) {
        if (feedback.approvalRequired) {
          addAlert(feedback.message, feedback.alertType);
        }
        await createLinkedTaskUpdate(
          selectedBoQ?.task,
          buildGeneralTransactionTaskUpdate({
            record: data,
            amount,
            transactionType,
          }),
        );
        onClose();
      } else {
        addAlert(feedback.message, feedback.alertType);
        // If API fails, allow user to try again
        setIsSaved(false);
      }
    } catch (error) {
      console.error("Error creating payment:", error);
      addAlert(error.message || "Failed to create payment", "error");
      // If there's an error, allow user to try again
      setIsSaved(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchTransactionCatagories = async () => {
      try {
        const accessToken = localStorage.getItem("access");
        const response = await fetch(
          `${BASE_URL}/finance/get-all-finance-catagory/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );
        if (response.ok) {
          const res = await response.json();
          const data = res.data;
          const purchaseCatagory = data.find(
            (cat) => cat.catagory_name === "General Transaction",
          );
          if (purchaseCatagory) {
            setOtherTransactionCatagory(purchaseCatagory.id);
          }
        } else {
          console.error("Failed to fetch material transaction catagories");
        }
      } catch (error) {
        console.error("Error fetching material transaction catagories:", error);
      }
    };
    fetchTransactionCatagories();
  }, []);

  // Fetch linked tasks
  useEffect(() => {
    const fetchLinkedTasks = async () => {
      try {
        const linkedTasks = await getLinkedTasksBySite();
        setBoqItems(linkedTasks);
      } catch (error) {
        console.error("Error fetching linked tasks:", error);
      }
    };
    fetchLinkedTasks();
  }, []);

  // Check if save button should be disabled
  const isSaveDisabled = !transactionType || !amount || isLoading || isSaved;

  const getNormalizedTaskStatus = (item) =>
    String(
      item?.task_status ||
        item?.taskStatus ||
        item?.status ||
        item?.task_details?.task_status ||
        item?.task_details?.status ||
        "",
    ).toLowerCase();
  const isCompletedTask = (item) =>
    getNormalizedTaskStatus(item).includes("complete");

  const filteredBoqItems = boqItems.filter(
    (item) =>
      !isCompletedTask(item) &&
      ((item.task_name || item.item_name || "")
        .toLowerCase()
        .includes(boqSearchQuery.toLowerCase()) ||
        item.sectionName.toLowerCase().includes(boqSearchQuery.toLowerCase())),
  );
  const getTaskStatusBucket = (item) => {
    const normalizedStatus = getNormalizedTaskStatus(item);

    if (
      normalizedStatus.includes("halt") ||
      normalizedStatus.includes("hold") ||
      normalizedStatus.includes("pause")
    ) {
      return "halted";
    }
    if (
      normalizedStatus.includes("not started") ||
      normalizedStatus.includes("not_started")
    ) {
      return "notStarted";
    }
    if (
      normalizedStatus.includes("ongoing") ||
      normalizedStatus.includes("in progress") ||
      normalizedStatus.includes("in_progress")
    ) {
      return "ongoing";
    }
    return "notStarted";
  };

  const groupedBoqItems = [
    { key: "ongoing", title: "Ongoing" },
    { key: "notStarted", title: "Not Started" },
    { key: "halted", title: "Halted" },
  ]
    .map((group) => ({
      ...group,
      items: filteredBoqItems.filter(
        (item) => getTaskStatusBucket(item) === group.key,
      ),
    }))
    .filter((group) => group.items.length > 0);

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="flex flex-row items-center justify-between px-3 h-16 py-2 border-b border-gray-100 relative shrink-0">
        <div />
        <h2 className="text-xl font-medium text-[#232323] text-center">
          General Payment
        </h2>
        <button
          onClick={onClose}
          className="w-8 h-8 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors"
          aria-label="Close"
        >
          <X size={20} />
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-2 py-3">
        <div className="space-y-6">
          <div className="rounded-xl border border-gray-100 bg-white p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#232323] mb-2">
                Purpose of Transaction
              </label>
              <input
                type="text"
                className="w-full p-2.5 text-sm bg-white rounded-lg border border-gray-200 mb-2"
                placeholder="Type"
                value={transactionType}
                onChange={(e) => setTransactionType(e.target.value)}
                disabled={isSaved}
              />
              <div className="flex flex-wrap gap-2">
                {transactionTypes.map((type) => (
                  <button
                    key={type}
                    onClick={() => setTransactionType(type)}
                    disabled={isSaved}
                    className={`px-3 py-1.5 rounded-full text-xs ${
                      transactionType === type
                        ? "bg-purple-100 text-purple-600"
                        : "bg-gray-100 text-gray-600"
                    } ${isSaved ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {boqItems.length > 0 && (
              <div className="pt-2 border-t border-gray-100">
                <label className="block text-sm font-medium text-[#232323] mb-2">
                  Link to Task (Optional)
                </label>
                <div
                  onClick={openBoQModal}
                  className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors border border-dashed ${
                    selectedBoQ
                      ? "bg-purple-50 border-purple-200"
                      : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                  }`}
                >
                  {selectedBoQ ? (
                    <div className="flex flex-col">
                      <span className="text-xs font-medium text-purple-700">
                        {selectedBoQ.task_name || selectedBoQ.item_name}
                      </span>
                      <span className="text-[11px] text-purple-500">
                        {selectedBoQ.sectionName}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center text-gray-500 text-xs">
                      <Link size={14} className="mr-2" />
                      Select a task to link
                    </div>
                  )}
                  <button
                    type="button"
                    className="text-purple-600 text-xs font-semibold"
                  >
                    {selectedBoQ ? "Change" : "Link"}
                  </button>
                </div>
                {selectedBoQ && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedBoQ(null);
                    }}
                    className="mt-1 text-[11px] text-red-500 hover:text-red-700 font-medium"
                  >
                    Remove link
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-6">
            <div className="space-y-6">
              <div className="rounded-xl border border-gray-100 bg-white p-4">
                <label className="block text-sm font-medium text-[#232323] mb-2">
                  Payment Date
                </label>
                <div className="flex gap-2">
                  <button
                    className={`px-3 py-1.5 rounded-full bg-purple-100 text-purple-600 text-xs ${
                      isSaved ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    onClick={handleNowButtonClick}
                    disabled={isSaved}
                  >
                    Now
                  </button>
                  <button
                    className={`px-3 py-1.5 rounded-full bg-gray-100 text-gray-600 text-xs flex items-center gap-2 ${
                      isSaved ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    onClick={toggleDatePicker}
                    disabled={isSaved}
                  >
                    Pick an older date
                    <Calendar className="h-4 w-4" />
                  </button>
                </div>

                {showDatePicker && (
                  <div className="mt-3">
                    <input
                      type="date"
                      className="w-full p-2 text-sm border border-gray-300 rounded-md"
                      value={transactionDate}
                      onChange={handleDateChange}
                      max={formattedCurrentDate}
                      disabled={isSaved}
                    />
                  </div>
                )}
              </div>

              <div className="rounded-xl border border-gray-100 bg-white p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#232323] mb-2">
                    ₹ Amount
                  </label>
                  <input
                    type="number"
                    min="0"
                    max={99_900_000_000}
                    className="w-full p-3 bg-white rounded-lg border border-gray-200"
                    placeholder="Enter amount"
                    value={amount}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "") {
                        setAmount("");
                        return;
                      }
                      const num = parseFloat(val);
                      if (num < 0) return;
                      if (num > 99_900_000_000) {
                        setAmount("99900000000");
                        return;
                      }
                      setAmount(val);
                    }}
                    disabled={isSaved}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {formatAmountPreview(amount)}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#232323] mb-2">
                    Payment Method
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {["Cash", "UPI/Bank Transfer", "Cheque"].map((method) => (
                      <button
                        key={method}
                        className={`px-3 py-1.5 rounded-full text-xs ${
                          paymentMethod === method
                            ? "bg-purple-100 text-purple-600"
                            : "bg-gray-100 text-gray-600"
                        } ${isSaved ? "opacity-50 cursor-not-allowed" : ""}`}
                        onClick={() => setPaymentMethod(method)}
                        disabled={isSaved}
                      >
                        {method}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="rounded-xl border border-gray-100 bg-white p-4">
                <label className="block text-sm font-medium text-[#232323] mb-2">
                  Remarks / Comments (Optional)
                </label>
                <input
                  type="text"
                  className="w-full p-2 text-sm bg-transparent border-b border-gray-300"
                  placeholder="Type"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  disabled={isSaved}
                />
              </div>

              <div className="rounded-xl border border-gray-100 bg-white p-4">
                <label className="block text-sm font-medium text-[#232323] mb-2">
                  Payment Slip (Optional)
                </label>
                {!previewUrl && (
                  <div className="flex gap-3">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      id="image-upload"
                      onChange={handleImageUpload}
                      disabled={isSaved}
                    />
                    <label
                      htmlFor="image-upload"
                      className={`flex-1 p-2.5 text-sm bg-purple-50 text-purple-600 rounded-md text-center cursor-pointer ${
                        isSaved ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      Upload Photo
                    </label>
                  </div>
                )}
                <div className="mt-3">
                  {previewUrl && (
                    <div className="relative w-full h-48 rounded-md border border-gray-200 bg-gray-50 overflow-hidden">
                      <img
                        src={previewUrl}
                        alt="Receipt"
                        className="w-full h-full object-contain rounded-md"
                      />
                      {!isSaved && (
                        <button
                          onClick={removeImage}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 border-t border-gray-100 flex flex-col items-center justify-center shrink-0 bg-white">
        <button
          className={`min-w-[240px] text-white font-medium py-2 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 whitespace-nowrap ${
            isSaveDisabled
              ? "bg-[#3B0069] cursor-not-allowed"
              : "bg-[#3B0069] hover:bg-[#3B0069D9]"
          }`}
          onClick={handleSave}
          disabled={isSaveDisabled}
          style={{ opacity: isSaveDisabled ? 0.4 : 1 }}
        >
          {isLoading ? "Saving..." : isSaved ? "Saved" : "Save"}
          <span className="rounded-full bg-[#58267F] p-1 flex items-center justify-center">
            {isLoading ? (
              <Loader className="h-4 w-4 animate-spin" />
            ) : (
              <Check size={18} />
            )}
          </span>
        </button>
      </div>

      {/* Task Selection Modal */}
      {showBoQModal && typeof document !== "undefined"
        ? createPortal(
            <div
              className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black bg-opacity-50"
              onClick={closeBoQModal}
            >
              <div
                className="bg-white rounded-xl w-full max-w-md max-h-[70vh] flex flex-col shadow-2xl overflow-hidden border border-gray-100"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50">
                  <div>
                    <h3 className="text-xl font-medium text-[#232323]">
                      Link to BoQ Task
                    </h3>
                  </div>
                  <button
                    type="button"
                    onClick={closeBoQModal}
                    className="p-1 rounded-full hover:bg-gray-200 text-gray-500 transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="p-4 border-b bg-white">
                  <div className="relative">
                    <Search
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                      size={16}
                    />
                    <input
                      type="text"
                      placeholder="Search BoQ items or sections..."
                      value={boqSearchQuery}
                      onChange={(e) => setBoqSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm outline-none transition-all"
                      autoFocus
                    />
                  </div>
                </div>

                <div className="overflow-y-auto flex-1 p-2 bg-white">
                  {groupedBoqItems.map((group) => (
                    <div key={group.key} className="mb-2">
                      <p className="px-1 py-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                        {group.title}
                      </p>
                      {group.items.map((item) => (
                        <div
                          key={item.id}
                          onClick={() => handleBoQSelect(item)}
                          className={`p-3 rounded-lg cursor-pointer mb-1 transition-all border ${
                            selectedBoQ?.id === item.id
                              ? "bg-purple-50 border-purple-200"
                              : "hover:bg-gray-50 border-transparent"
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="text-sm font-semibold text-gray-900">
                                {item.task_name || item.item_name}
                              </div>
                              <div className="text-[11px] text-gray-500 mt-0.5">
                                {item.sectionName}
                              </div>
                            </div>
                            {selectedBoQ?.id === item.id && (
                              <div className="bg-purple-600 h-2 w-2 rounded-full"></div>
                            )}
                          </div>
                          {/* <div className="mt-2 flex items-center">
                            <span className="text-[10px] font-medium bg-gray-100 px-2 py-0.5 rounded-md text-gray-600">
                              {item.quantity} {item.uom}
                            </span>
                          </div> */}
                        </div>
                      ))}
                    </div>
                  ))}

                  {filteredBoqItems.length === 0 && (
                    <div className="py-12 text-center">
                      <p className="text-gray-400 text-sm">
                        No matching tasks found
                      </p>
                    </div>
                  )}
                </div>

                <div className="p-4 border-t bg-gray-50 flex gap-3">
                  <button
                    type="button"
                    onClick={closeBoQModal}
                    className="flex-1 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
